import { apiCall } from '@/lib/api';
import { setDateTimePrefs } from '@/lib/datetime';
import { createQuery } from '@tanstack/solid-query';
import {
  ApiClientError,
  type DateFormat,
  type TimeFormat,
  getMe,
} from '@uchat-team-management/shared';
import { type User as FirebaseUser, onAuthStateChanged } from 'firebase/auth';
import {
  type Accessor,
  type JSX,
  createContext,
  createEffect,
  createSignal,
  onCleanup,
  onMount,
  useContext,
} from 'solid-js';
import { getFirebaseAuth } from './firebase';

export interface SessionUser {
  id: string;
  firebaseUid: string;
  email: string;
  displayName: string;
  mustSetName: boolean;
  welcomedAt: string | null;
  githubUsername: string | null;
  githubUsernameLocked: boolean;
  githubName: string | null;
  githubAvatarUrl: string | null;
  dateFormat: DateFormat | null;
  timeFormat: TimeFormat | null;
  timezone: string | null;
  slackUserId: string | null;
  slackLinkedAt: string | null;
  slackOpenUrl: string | null;
  roles: { id: string; name: string; isSystem: boolean }[];
  permissions: Set<string>;
}

export interface RejectedAccount {
  email: string;
}

interface AuthContextValue {
  firebaseUser: Accessor<FirebaseUser | null | undefined>;
  session: Accessor<SessionUser | null | undefined>;
  rejected: Accessor<RejectedAccount | null>;
  setRejected: (value: RejectedAccount | null) => void;
  sessionError: Accessor<unknown>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export const AuthProvider = (props: { children: JSX.Element }): JSX.Element => {
  const [firebaseUser, setFirebaseUser] = createSignal<FirebaseUser | null | undefined>(undefined);
  const [rejected, setRejected] = createSignal<RejectedAccount | null>(null);

  onMount(() => {
    const unsub = onAuthStateChanged(getFirebaseAuth(), (u) => {
      setFirebaseUser(u);
      if (u) {
        // A fresh Firebase user means the rejected banner (if any) is stale.
        setRejected(null);
      }
    });
    onCleanup(unsub);
  });

  const meQuery = createQuery(() => ({
    enabled: firebaseUser() != null,
    queryKey: ['auth', 'me'] as const,
    queryFn: () => apiCall(getMe, {}),
    retry: (count: number, err: unknown) => {
      if (err instanceof ApiClientError && err.status < 500) return false;
      return count < 1;
    },
  }));

  // Feed display preferences into the shared datetime formatter.
  createEffect(() => {
    const data = meQuery.data?.data;
    setDateTimePrefs({
      dateFormat: data?.dateFormat ?? null,
      timeFormat: data?.timeFormat ?? null,
      timezone: data?.timezone ?? null,
    });
  });

  const session = (): SessionUser | null | undefined => {
    if (firebaseUser() === undefined) return undefined;
    if (firebaseUser() === null) return null;
    if (meQuery.isPending) return undefined;
    if (meQuery.isError) return null;
    const data = meQuery.data?.data;
    if (!data) return null;
    return {
      id: data.id,
      firebaseUid: data.firebaseUid,
      email: data.email,
      displayName: data.displayName,
      mustSetName: data.mustSetName,
      welcomedAt: data.welcomedAt,
      githubUsername: data.githubUsername,
      githubUsernameLocked: data.githubUsernameLocked,
      githubName: data.githubName,
      githubAvatarUrl: data.githubAvatarUrl,
      dateFormat: data.dateFormat,
      timeFormat: data.timeFormat,
      timezone: data.timezone,
      slackUserId: data.slackUserId,
      slackLinkedAt: data.slackLinkedAt,
      slackOpenUrl: data.slackOpenUrl,
      roles: data.roles,
      permissions: new Set(data.permissions),
    };
  };

  const refresh = async (): Promise<void> => {
    await meQuery.refetch();
  };

  const value: AuthContextValue = {
    firebaseUser,
    session,
    rejected,
    setRejected,
    sessionError: () => meQuery.error,
    refresh,
  };

  return <AuthContext.Provider value={value}>{props.children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextValue => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
};
